/**
 * Dashboard Screen
 *
 * Shows portfolio total value, thesis coverage, sector and geographic breakdowns.
 * i18n SUPPORTED
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  TouchableOpacity,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import { useTheme } from '../../contexts/ThemeContext';
import { getDashboardData } from '../../services/dashboardApi';
import type {
  DashboardData,
  SectorBreakdown,
  GeographicBreakdown,
  AssetCategoryBreakdown,
  StrategyBreakdown,
} from '../../services/dashboardApi';
import { PieChart } from 'react-native-chart-kit';
import { RiskWidget } from '../../components/risk/RiskWidget';
import { ThemeExposureChart } from '../../components/dashboard/ThemeExposureChart';
import type { DashboardNavigatorParamList } from '../../navigation/DashboardNavigator';

const CHART_HEIGHT = 200;

export function DashboardScreen() {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const navigation = useNavigation<StackNavigationProp<DashboardNavigatorParamList>>();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const dashboardData = await getDashboardData();
      setData(dashboardData);
    } catch (err) {
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text style={[styles.loadingText, { color: theme.colors.textSecondary }]}>
            {t('common.loading')}
          </Text>
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <View style={styles.centerContainer}>
          <Text style={[styles.errorText, { color: theme.colors.error }]}>
            {t('common.error')}: {error}
          </Text>
          <TouchableOpacity
            style={[styles.retryButton, { backgroundColor: theme.colors.primary }]}
            onPress={loadDashboardData}
          >
            <Text style={styles.retryButtonText}>{t('common.retry')}</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  // Use chart colors from theme
  const generateColors = (count: number): string[] => {
    return theme.colors.chartColors.slice(0, count);
  };

  const chartConfig = {
    backgroundGradientFrom: 'transparent',
    backgroundGradientTo: 'transparent',
    color: (opacity = 1) => {
      const rgb = theme.id === 'bauhaus' ? '120, 120, 120' : '250, 250, 250';
      return `rgba(${rgb}, ${opacity})`;
    },
    labelColor: (opacity = 1) => {
      const rgb = theme.id === 'bauhaus' ? '80, 80, 80' : '250, 250, 250';
      return `rgba(${rgb}, ${opacity})`;
    },
  };

  const sectorChartData = data?.sector_breakdown?.map((item, index) => ({
    name: t(`sectors.${item.sector}`, { defaultValue: item.sector }),
    population: item.value,
    color: generateColors(data.sector_breakdown.length)[index],
    legendFontColor: theme.colors.text,
    legendFontSize: 12,
  }));

  const regionChartData = data?.geographic_breakdown?.map((item, index) => ({
    name: t(`countries.${item.country}`, { defaultValue: item.country }),
    population: item.value,
    color: generateColors(data.geographic_breakdown.length)[index],
    legendFontColor: theme.colors.text,
    legendFontSize: 12,
  }));

  const assetCategoryChartData = data?.asset_category_breakdown?.map((item, index) => ({
    name: t(`assetCategories.${item.asset_category}`, { defaultValue: item.asset_category }),
    population: item.value,
    color: generateColors(data.asset_category_breakdown.length)[index],
    legendFontColor: theme.colors.text,
    legendFontSize: 12,
  }));

  const strategyChartData = data?.strategy_breakdown?.map((item, index) => ({
    name: t(`dashboard.strategy${item.strategy}`, { defaultValue: item.strategy }),
    population: item.value,
    color: generateColors(data.strategy_breakdown.length)[index],
    legendFontColor: theme.colors.text,
    legendFontSize: 12,
  }));

  const chartWidth = 200;
  const chartRadius = 65;
  const paddingLeft = 35;

  const formatValue = (value: number): string => {
    return Math.round(value).toLocaleString();
  };

  // Color-code thesis coverage: red < 20%, warning 20-50%, success 50-80%, primary > 80%
  const getThesisCoverageColor = (coverage: number): string => {
    if (coverage < 20) return theme.colors.error;
    if (coverage < 50) return theme.colors.warning;
    if (coverage < 80) return theme.colors.success;
    return theme.colors.primary;
  };

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
      contentContainerStyle={styles.content}
    >
      <RiskWidget onPress={() => navigation.navigate('Risk')} />
      <TouchableOpacity
        style={styles.entryRow}
        onPress={() => navigation.navigate('Calibration')}
        accessibilityRole="button"
      >
        <Text style={styles.entryRowTitle}>{t('calibration.title')}</Text>
        <Text style={styles.entryRowArrow}>›</Text>
      </TouchableOpacity>
      {/* Top themes by exposure, ahead of the sector/region pies: it is the only
          card that answers "what am I actually exposed to", and it is a ranking
          rather than an allocation, so it states its own basis. */}
      <ThemeExposureChart />
      {/* Top Section - Side by Side Cards */}
      <View style={styles.topSection}>
        {/* Total Portfolio Value Card */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>{t('dashboard.portfolioValue')}</Text>
          <Text style={styles.heroValue}>
            {/* total_value_base_currency is each position's market value FX-converted
                to the user's base currency, then summed. total_value is the raw sum of
                native-currency values (mixed units) and must NOT be shown as the total. */}
            {data?.base_currency} {formatValue(data?.total_value_base_currency)}
          </Text>
        </View>

        {/* Thesis Coverage Card */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>
            {t('dashboard.thesisCoverage', { defaultValue: 'Thesis Coverage' })}
          </Text>
          <View style={styles.thesisRow}>
            <Text
              style={[
                styles.heroPercentage,
                { color: getThesisCoverageColor(data?.thesis_coverage ?? 0) },
              ]}
            >
              {data?.thesis_coverage !== undefined ? `${data.thesis_coverage.toFixed(0)}%` : '—'}
            </Text>
            <Text style={styles.thesisCount}>
              {data?.positions_with_thesis}/{data?.total_positions}
            </Text>
          </View>
        </View>
      </View>

      {/* Conviction Strategy Breakdown Card */}
      <View style={styles.chartCard}>
        <Text style={styles.chartCardTitle}>
          {t('dashboard.strategyBreakdown', { defaultValue: 'Conviction Strategy' })}
        </Text>
        {data?.strategy_breakdown && data.strategy_breakdown.length > 0 ? (
          <View style={styles.chartSection}>
            <View style={styles.chartContainer}>
              <PieChart
                data={strategyChartData}
                width={chartWidth}
                height={CHART_HEIGHT}
                chartConfig={chartConfig}
                accessor="population"
                backgroundColor="#00000000"
                paddingLeft={paddingLeft}
                hasLegend={false}
                donut
                radius={chartRadius}
              />
            </View>
            <View style={styles.legendWrap}>
              {data.strategy_breakdown.map((item, index) => (
                <View key={item.strategy} style={styles.legendChip}>
                  <View
                    style={[
                      styles.legendDot,
                      { backgroundColor: generateColors(data.strategy_breakdown.length)[index] },
                    ]}
                  />
                  <Text style={[styles.legendText, { color: theme.colors.text }]} numberOfLines={1}>
                    {t(`dashboard.strategy${item.strategy}`, { defaultValue: item.strategy })}
                  </Text>
                  <Text style={[styles.legendPercent, { color: theme.colors.textSecondary }]}>
                    {item.percentage.toFixed(1)}%
                  </Text>
                </View>
              ))}
            </View>
          </View>
        ) : (
          <Text style={styles.emptyText}>
            {t('dashboard.noBreakdownAvailable', { defaultValue: 'No breakdown available' })}
          </Text>
        )}
      </View>

      {/* Sector Breakdown Card */}
      <View style={styles.chartCard}>
        <Text style={styles.chartCardTitle}>{t('dashboard.sectorBreakdown')}</Text>
        {data?.sector_breakdown && data.sector_breakdown.length > 0 ? (
          <View style={styles.chartSection}>
            <View style={styles.chartContainer}>
              <PieChart
                data={sectorChartData}
                width={chartWidth}
                height={CHART_HEIGHT}
                chartConfig={chartConfig}
                accessor="population"
                backgroundColor="#00000000"
                paddingLeft={paddingLeft}
                hasLegend={false}
                donut
                radius={chartRadius}
              />
            </View>
            <View style={styles.legendWrap}>
              {data.sector_breakdown.map((item, index) => (
                <View key={item.sector} style={styles.legendChip}>
                  <View
                    style={[
                      styles.legendDot,
                      { backgroundColor: generateColors(data.sector_breakdown.length)[index] },
                    ]}
                  />
                  <Text style={[styles.legendText, { color: theme.colors.text }]} numberOfLines={1}>
                    {t(`sectors.${item.sector}`, { defaultValue: item.sector })}
                  </Text>
                  <Text style={[styles.legendPercent, { color: theme.colors.textSecondary }]}>
                    {item.percentage.toFixed(1)}%
                  </Text>
                </View>
              ))}
            </View>
          </View>
        ) : (
          <Text style={styles.emptyText}>
            {t('dashboard.noBreakdownAvailable', { defaultValue: 'No breakdown available' })}
          </Text>
        )}
      </View>

      {/* Asset Category Breakdown Card */}
      <View style={styles.chartCard}>
        <Text style={styles.chartCardTitle}>
          {t('dashboard.assetBreakdown', { defaultValue: 'Asset Breakdown' })}
        </Text>
        {data?.asset_category_breakdown && data.asset_category_breakdown.length > 0 ? (
          <View style={styles.chartSection}>
            <View style={styles.chartContainer}>
              <PieChart
                data={assetCategoryChartData}
                width={chartWidth}
                height={CHART_HEIGHT}
                chartConfig={chartConfig}
                accessor="population"
                backgroundColor="#00000000"
                paddingLeft={paddingLeft}
                hasLegend={false}
                donut
                radius={chartRadius}
              />
            </View>
            <View style={styles.legendWrap}>
              {data.asset_category_breakdown.map((item, index) => (
                <View key={item.asset_category} style={styles.legendChip}>
                  <View
                    style={[
                      styles.legendDot,
                      {
                        backgroundColor: generateColors(data.asset_category_breakdown.length)[
                          index
                        ],
                      },
                    ]}
                  />
                  <Text style={[styles.legendText, { color: theme.colors.text }]} numberOfLines={1}>
                    {t(`assetCategories.${item.asset_category}`, {
                      defaultValue: item.asset_category,
                    })}
                  </Text>
                  <Text style={[styles.legendPercent, { color: theme.colors.textSecondary }]}>
                    {item.percentage.toFixed(1)}%
                  </Text>
                </View>
              ))}
            </View>
          </View>
        ) : (
          <Text style={styles.emptyText}>
            {t('dashboard.noBreakdownAvailable', { defaultValue: 'No breakdown available' })}
          </Text>
        )}
      </View>

      {/* Geographic Breakdown Card */}
      <View style={styles.chartCard}>
        <Text style={styles.chartCardTitle}>{t('dashboard.regionBreakdown')}</Text>
        {data?.geographic_breakdown && data.geographic_breakdown.length > 0 ? (
          <View style={styles.chartSection}>
            <View style={styles.chartContainer}>
              <PieChart
                data={regionChartData}
                width={chartWidth}
                height={CHART_HEIGHT}
                chartConfig={chartConfig}
                accessor="population"
                backgroundColor="#00000000"
                paddingLeft={paddingLeft}
                hasLegend={false}
                donut
                radius={chartRadius}
              />
            </View>
            <View style={styles.legendWrap}>
              {data.geographic_breakdown.map((item, index) => (
                <View key={item.country} style={styles.legendChip}>
                  <View
                    style={[
                      styles.legendDot,
                      { backgroundColor: generateColors(data.geographic_breakdown.length)[index] },
                    ]}
                  />
                  <Text style={[styles.legendText, { color: theme.colors.text }]} numberOfLines={1}>
                    {t(`countries.${item.country}`, { defaultValue: item.country })}
                  </Text>
                  <Text style={[styles.legendPercent, { color: theme.colors.textSecondary }]}>
                    {item.percentage.toFixed(1)}%
                  </Text>
                </View>
              ))}
            </View>
          </View>
        ) : (
          <Text style={styles.emptyText}>
            {t('dashboard.noBreakdownAvailable', { defaultValue: 'No breakdown available' })}
          </Text>
        )}
      </View>
    </ScrollView>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    entryRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: theme.colors.surface,
      borderRadius: theme.radii.lg,
      borderWidth: 1,
      borderColor: theme.colors.border,
      padding: theme.spacing.lg,
      marginBottom: theme.spacing.sm,
    },
    entryRowTitle: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    entryRowArrow: {
      color: theme.colors.textSecondary,
      fontSize: theme.typography.fontSizeLg,
    },
    container: {
      flex: 1,
    },
    content: {
      padding: 16,
      paddingBottom: 32,
    },
    centerContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 48,
    },
    loadingText: {
      marginTop: 12,
      fontSize: theme.typography.fontSizeMd,
      color: theme.colors.textSecondary,
    },
    errorText: {
      fontSize: theme.typography.fontSizeMd,
      color: theme.colors.error,
      textAlign: 'center',
      marginBottom: 16,
    },
    retryButton: {
      paddingHorizontal: 24,
      paddingVertical: 12,
      borderRadius: theme.radii.lg,
      backgroundColor: theme.colors.primary,
      alignItems: 'center',
      marginTop: 16,
    },
    retryButtonText: {
      color: theme.colors.textInverse,
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    // Top section - two summary cards side by side
    topSection: {
      flexDirection: 'row',
      gap: 12,
      marginBottom: 16,
    },
    card: {
      flex: 1,
      backgroundColor: theme.colors.surface,
      borderRadius: theme.radii.lg,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderColor: theme.colors.border,
      padding: 12,
    },
    cardTitle: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
      color: theme.colors.textSecondary,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      marginBottom: 2,
    },
    heroValue: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
      color: theme.colors.text,
    },
    thesisRow: {
      flexDirection: 'row',
      alignItems: 'baseline',
      gap: 6,
    },
    heroPercentage: {
      fontSize: 24,
      fontWeight: theme.typography.fontWeightBold,
    },
    thesisCount: {
      fontSize: theme.typography.fontSizeXs,
      color: theme.colors.textTertiary,
    },
    // Chart cards
    chartCard: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.radii.lg,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderColor: theme.colors.border,
      padding: 16,
      marginBottom: 16,
    },
    chartCardTitle: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightBold,
      color: theme.colors.text,
    },
    chartSection: {
      alignItems: 'center',
      paddingTop: 8,
    },
    chartContainer: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    // Legend - horizontal wrapping chips
    legendWrap: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 6,
      marginTop: 8,
    },
    legendChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    legendDot: {
      width: 8,
      height: 8,
      borderRadius: 4,
    },
    legendText: {
      fontSize: theme.typography.fontSizeXs,
    },
    legendPercent: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    emptyText: {
      fontSize: theme.typography.fontSizeMd,
      color: theme.colors.textSecondary,
      textAlign: 'center',
      paddingVertical: 24,
    },
  });
}
